import React from "react";
import { I18n } from "react-redux-i18n";

import { HttpErrorDto } from "@common/dto/http";
import { ColumnListDto } from "@common/dto/column/list-dto";
import { useInvitesController } from "./invites";
import { AdminSmartListFunctionGetDto } from "@common/dto/admin-function/get";
import { getAdminSmartListFunctions } from "@common/actions/permissions";
import {
  SendInviteParams,
  sendInvite as sendInviteIndividuals,
} from "@common/actions/individuals";
import { useTableFiltersController } from "@common/hooks/table/filters";
import { getFilters } from "@common/actions/Filters";
import { useMessage } from "@common/hooks/message";
import { usePatientInviteController } from "./patient-invite";
import {
  InviteStatusFilter,
  InvitesExportParams,
  exportInvites,
  getInvites,
} from "../services/invites";
import {
  InvitesExportOptionKeys,
  InvitesExportType,
} from "../components/invites-export/invites-export.types";
import { INVITES_EXPORT_DELIMITER } from "../constants/shared";
import { downloadFile } from "@common/utils/file";
import { NewTableSortingDirection } from "@common/components/new-table";
import {
  FormatInfo,
  QueryManagementType,
} from "@common/components/query-management";
import {
  getInvitesColumns,
  InviteSendParams,
  sendInvite,
} from "@common/actions/invites";
import { getQueryFormat } from "@modules/invite-status/services/formats";
import { InviteListDto } from "@common/dto/invite/list-dto";

export const useController = () => {
  const {
    showErrorMessage,
    messageProps,
    closeMessage,
    showInfoMessage,
    showConfirmMessage,
    showMessage,
  } = useMessage();

  const [adminFunctions, setAdminFunctions] = React.useState<
    AdminSmartListFunctionGetDto | undefined
  >();
  const [invitesExportDialogOpened, setInvitesExportDialogOpened] =
    React.useState(false);
  const [loadQueryManagementType, setLoadQueryManagementType] = React.useState<
    QueryManagementType | undefined
  >();
  const [queryCreatingDialogOpened, setQueryCreatingDialogOpened] =
    React.useState(false);

  const [inviteStatusFilter, setInviteStatusFilter] =
    React.useState<InviteStatusFilter>({
      questionnaires: [],
      inviteStatuses: [],
      invitedBy: [],
      dateSent: undefined,
      dateStarted: undefined,
      dateCompleted: undefined,
    });

  const [clearQueryBtnDisabled, setClearQueryBtnDisabled] =
    React.useState<boolean>(true);

  const [allInvites, setAllInvites] = React.useState<InviteListDto[]>([]);

  const openQueryCreatingDialog = React.useCallback(() => {
    setQueryCreatingDialogOpened(true);
  }, []);

  const defaultActions = [
    {
      label: I18n.t("Load Query"),
      onClick: () => setLoadQueryManagementType(QueryManagementType.load),
    },
    { label: I18n.t("New Query"), onClick: openQueryCreatingDialog },
    {
      label: I18n.t("Edit Query"),
      onClick: () => setLoadQueryManagementType(QueryManagementType.edit),
    },
  ];

  const [recentQueryFormats, setRecentQueryFormats] = React.useState<
    { label: string; onClick: () => void }[]
  >([]);

  const queryFormatsActions = React.useMemo(() => {
    const combined = [...recentQueryFormats, ...defaultActions];
    const seen = new Set();
    return combined.filter((item) => {
      if (seen.has(item.label)) return false;
      seen.add(item.label);
      return true;
    });
  }, [recentQueryFormats]);

  const { patientInviteOpened, openPatientInvite, closePatientInvite } =
    usePatientInviteController({ showInfoMessage });

  const getColumnId = React.useCallback((column: ColumnListDto) => {
    return `field_id__${column.field.id.toString()}__field_type__${column.field.type.toString()}__context__${column.field.context.toString()}`;
  }, []);

  const {
    handleInviteSelectedItemsChange,
    handleLaunchNewQuestionnaire,
    getQuestionnairesForNewQuestionnaire,
    invites,
    invitesCount,
    invitesColumns,
    invitesLoadMetadata,
    invitesSelectedItems,
    invitesLoading,
    inviteItemUniqProperty,
    handleUpdateInvitesLockStatus,
    handleInvitesColumnsOrderChange,
    handleRestoreInvitesColumnsToDefault,
    getAndSetInvitesColumns,
    handleGetInvites,
    updateInvitesColumns,
    onRefreshInvitesOrChangeInvitesColumns,
    handleUpdateInviteStatus,
    handleUpdateReviewedStatusActions,
    getFieldsForInvitesColumn,
    getFoldersForInvitesColumn,
    invitesSearchKeyword,
    INVITES_LOAD_ITEMS_COUNT,
  } = useInvitesController({
    getColumnId,
    showErrorMessage,
  });

  const { filters, initializeFilters } = useTableFiltersController({
    getFilters,
  });

  React.useEffect(() => {
    initialize();
  }, []);

  const initialize = () => {
    getAdminSmartListFunctions()
      .then((res) => {
        setAdminFunctions(res);
      })
      .catch((err) => {
        console.error(err);
        const errorMessage =
          (err as HttpErrorDto | undefined)?.entity ||
          I18n.t("Failed to get admin functions");
        showErrorMessage(errorMessage);
      });

    initializeFilters().catch((err) => {
      console.error(err);
      const errorMessage =
        (err as HttpErrorDto | undefined)?.entity ||
        I18n.t("Failed to get filters");
      showErrorMessage(errorMessage);
    });

    getAndSetInvitesColumns()
      .then((columns) => {
        const CONSTANT_ID_FOR_ALL_ENVS_FOR_DATE_INVITED_COL = 82;
        const dateInvitedColumn = columns.find(
          (col) =>
            col.field.id === CONSTANT_ID_FOR_ALL_ENVS_FOR_DATE_INVITED_COL
        );
        handleGetInvites({
          columns: columns,
          loadedInvites: [],
          sorting: dateInvitedColumn
            ? {
                id: getColumnId(dateInvitedColumn),
                direction: NewTableSortingDirection.desc,
              }
            : undefined,
          filtering: undefined,
          pagination: {
            start: 0,
            size: INVITES_LOAD_ITEMS_COUNT,
          },
          ...inviteStatusFilter,
        }).catch((err) => {
          console.error(err);
          const errorMessage =
            (err as HttpErrorDto)?.entity || I18n.t("Failed to get invites");
          showErrorMessage(errorMessage);
        });
      })
      .catch((err) => {
        console.error(err);
        const errorMessage =
          (err as HttpErrorDto | undefined)?.entity ||
          I18n.t("Failed to get invites columns");
        showErrorMessage(errorMessage);
      });
  };

  const handleInvitesSearch = async ({
    searchKeyword,
  }: {
    searchKeyword: string;
  }) => {
    try {
      await handleGetInvites({
        searchKeyword,
        columns: invitesColumns,
        loadedInvites: allInvites,
        filtering: invitesLoadMetadata.filtering,
        sorting: invitesLoadMetadata.sorting,
        pagination: {
          size: invitesLoadMetadata.pagination.size,
          start: 0,
        },
        ...inviteStatusFilter,
      });
      onRefreshInvitesOrChangeInvitesColumns();
    } catch (err) {
      console.error(err);
      const errorMessage =
        (err as HttpErrorDto | undefined)?.entity ||
        I18n.t("Failed to get invites");
      showErrorMessage(errorMessage);
    }
  };

  const handleGetFilteredInvites = async (filter: InviteStatusFilter) => {
    try {
      await handleGetInvites({
        searchKeyword: invitesSearchKeyword,
        columns: invitesColumns,
        loadedInvites: [],
        filtering: invitesLoadMetadata.filtering,
        sorting: invitesLoadMetadata.sorting,
        pagination: {
          size: invitesLoadMetadata.pagination.size,
          start: 0,
        },
        ...filter,
      });
      setInviteStatusFilter(filter);
      onRefreshInvitesOrChangeInvitesColumns();
    } catch (err) {
      console.error(err);
      const errorMessage =
        (err as HttpErrorDto | undefined)?.entity ||
        I18n.t("Failed to get invites");
      showErrorMessage(errorMessage);
    }
  };

  const handleNewInvitesSend = async (data: Array<SendInviteParams>) => {
    await Promise.all(data.map(sendInviteIndividuals));

    handleGetInvites({
      searchKeyword: invitesSearchKeyword,
      columns: invitesColumns,
      loadedInvites: [],
      sorting: invitesLoadMetadata.sorting,
      filtering: invitesLoadMetadata.filtering,
      pagination: {
        start: 0,
        size: INVITES_LOAD_ITEMS_COUNT,
      },
      ...inviteStatusFilter,
    })
      .then(() => {
        onRefreshInvitesOrChangeInvitesColumns();
      })
      .catch((err) => {
        console.error(err);
        const errorMessage =
          (err as HttpErrorDto)?.entity || I18n.t("Failed to get invites");
        showErrorMessage(errorMessage);
      });
  };

  const handlePatientInviteSubmit = async (data: InviteSendParams) => {
    await sendInvite(data);

    handleGetInvites({
      columns: invitesColumns,
      loadedInvites: [],
      searchKeyword: invitesSearchKeyword,
      sorting: invitesLoadMetadata.sorting,
      filtering: invitesLoadMetadata.filtering,
      pagination: {
        start: 0,
        size: INVITES_LOAD_ITEMS_COUNT,
      },
      ...inviteStatusFilter,
    });

    closePatientInvite();
    showInfoMessage(
      I18n.t("The Questionnaire invitation emails were successfully submitted")
    );
  };

  // TODO below code related to export should be in a separate file

  const closeInvitesExportDialog = React.useCallback(() => {
    setInvitesExportDialogOpened(false);
  }, []);

  const openInvitesExportDialog = React.useCallback(() => {
    setInvitesExportDialogOpened(true);
  }, []);

  const closeLoadQueryManagement = () => {
    setLoadQueryManagementType(undefined);
  };

  const closeQueryCreatingDialog = React.useCallback(() => {
    setQueryCreatingDialogOpened(false);
  }, []);

  const handleSubmitInvitesExport = React.useCallback(
    async (params: {
      options: Array<InvitesExportOptionKeys>;
      setting: {
        exportType: InvitesExportType;
        delimiter?: string;
      };
    }) => {
      const { options, setting } = params;

      const config = options.reduce((acc, current) => {
        acc[current] = true;
        return acc;
      }, {} as Record<InvitesExportOptionKeys, boolean>);

      const defaultDelimiter = ",";

      const payload: InvitesExportParams = {
        exportType: setting.exportType,
        delimiter:
          INVITES_EXPORT_DELIMITER[setting.exportType] ||
          setting.delimiter ||
          defaultDelimiter,
        ...config,
        smartList: {
          columns: invitesColumns,
        },
      };

      const { filename, entity } = await exportInvites(payload);
      const filenameWithPrefix = `invites_${filename}`;

      downloadFile(entity, filenameWithPrefix);

      closeInvitesExportDialog();
    },
    [closeInvitesExportDialog, invitesColumns]
  );

  const applyQueryFormatFilter = async (
    format: any,
    setClearQueryBtnDisabled: (val: boolean) => void,
    inviteStatusFilter: InviteStatusFilter,
    showErrorMessage: (msg: string) => void
  ) => {
    try {
      const cleanedClauses = (format?.clauses || []).map(
        (clause: { andOR?: any; [key: string]: any }) => {
          const { andOR, ...rest } = clause;
          return rest;
        }
      );

      const pagination = {
        start: 0,
        size: 50,
      };
      handleGetInvites({
        columns: invitesColumns,
        loadedInvites: allInvites,
        sorting: invitesLoadMetadata.sorting,
        pagination: {
          size: invitesLoadMetadata.pagination.size,
          start: 0,
        },
        searchKeyword: invitesSearchKeyword,
        questionnaires: [],
        inviteStatuses: [],
        invitedBy: [],
        clauses: cleanedClauses,
        ...inviteStatusFilter,
      }).catch((err) => {
        console.error(err);
        showErrorMessage(I18n.t("Failed to get invites"));
      });
      setClearQueryBtnDisabled(false);
    } catch (err) {
      console.error(err);
      showErrorMessage(I18n.t("Failed to apply query filter"));
    }
  };

  const handleQueryManagementSubmit = async (formatInfo: FormatInfo) => {
    let response: any;

    try {
      response = await getQueryFormat(formatInfo.format.id);

      await applyQueryFormatFilter(
        response,
        setClearQueryBtnDisabled,
        inviteStatusFilter,
        showErrorMessage
      );
    } catch (e) {
      console.error(e);
      showErrorMessage(I18n.t("Failed to apply query filter"));
    } finally {
      closeLoadQueryManagement();

      if (response?.name) {
        const newItem = {
          label: response.name,
          onClick: async () => {
            await applyQueryFormatFilter(
              response,
              setClearQueryBtnDisabled,
              inviteStatusFilter,
              showErrorMessage
            );
          },
        };

        setRecentQueryFormats((prev) => {
          const updated = [
            newItem,
            ...prev.filter((item) => item.label !== newItem.label),
          ];
          return updated;
        });
      }
    }
  };

  const handleClearQueryFormatFilter = () => {
    setClearQueryBtnDisabled(true);
    setAllInvites([]);
    initialize();
  };

  return {
    INVITES_LOAD_ITEMS_COUNT,
    handleInvitesSearch,
    invitesSearchKeyword,
    handleInviteSelectedItemsChange,
    handleLaunchNewQuestionnaire,
    getQuestionnairesForNewQuestionnaire,
    adminFunctions,
    filters,
    getFoldersForInvitesColumn,
    showMessage,
    inviteItemUniqProperty,
    invitesLoading,
    invitesSelectedItems,
    invitesLoadMetadata,
    invitesCount,
    invitesColumns,
    invites,
    handleNewInvitesSend,
    handleRestoreInvitesColumnsToDefault,
    updateInvitesColumns,
    handleInvitesColumnsOrderChange,
    handleGetInvites,
    getFieldsForInvitesColumn,
    messageProps,
    getColumnId,
    showErrorMessage,
    closeMessage,
    showInfoMessage,
    handleUpdateInvitesLockStatus,
    handleUpdateInviteStatus,
    handleUpdateReviewedStatusActions,
    openPatientInvite,
    patientInviteOpened,
    closePatientInvite,
    handlePatientInviteSubmit,
    inviteStatusFilter,
    setInviteStatusFilter,
    invitesExportDialogOpened,
    closeInvitesExportDialog,
    openInvitesExportDialog,
    handleSubmitInvitesExport,
    closeLoadQueryManagement,
    loadQueryManagementType,
    handleQueryManagementSubmit,
    closeQueryCreatingDialog,
    openQueryCreatingDialog,
    queryCreatingDialogOpened,
    showConfirmMessage,
    handleGetFilteredInvites,
    queryFormatsActions,
    clearQueryBtnDisabled,
    allInvites,
    handleClearQueryFormatFilter,
  };
};
